import { ScanLine, ShoppingCart, LayoutGrid, AlertTriangle } from 'lucide-react'
import { useIsMobile, useBlockedCartLines } from '@/hooks'
import { usePOSStore } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import { formatCurrency } from '@/lib/utils'

interface MobileCartBarProps {
  /** Opens the camera barcode scanner (managed by the POS route). */
  onScanClick: () => void
}

// Mobile-only bottom action bar. Two thumb-reachable affordances:
//   - Cart bar (left, flex-1): live count + total, tap to open the full-screen
//     cart (activeTab='cart'). Shown only when the cart has items. A Cart
//     affordance, NOT a Checkout shortcut — the cart view is where the cashier
//     reviews items / customer / limits / discounts before tendering, and the
//     Checkout button lives there. Mirrors CartSummary's green Checkout button.
//   - Scan button (right): opens the camera barcode scanner (scan -> add to
//     cart). Always shown — so the first item can be scanned into an empty cart.
//
// Hidden on desktop and on the cart view itself. Rendered as a flex child at
// the bottom of <main> so it never overlaps the scrolling grid.
export function MobileCartBar({ onScanClick }: MobileCartBarProps) {
  const isMobile = useIsMobile()
  const cart = usePOSStore((s) => s.cart)
  const activeTab = usePOSStore((s) => s.activeTab)
  const serverTotals = usePOSStore((s) => s.serverTotals)
  const setActiveTab = usePOSStore((s) => s.setActiveTab)
  const openQuickActions = useUIStore((s) => s.openQuickActions)
  const previewTotal = useCartStore((s) => s.preview?.totals?.total ?? null)
  // The total on this bar excludes any line /cart/preview refused to price, so
  // on a blocked cart it reads $0.00 with nothing to explain it from here. The
  // reason lives on the cart view; flag the bar so the cashier goes there.
  const { hasBlocked } = useBlockedCartLines()

  if (!isMobile || activeTab === 'cart') return null

  const hasItems = cart.length > 0
  const itemCount = cart.reduce((sum, item) => sum + Math.abs(item.quantity), 0)

  // Mirror CartSummary's total priority: BE-confirmed serverTotals (refund /
  // partial), else the last /cart/preview total, else a local line-sum for the
  // brief window before the first preview returns.
  const localSubtotal = cart.reduce((acc, item) => {
    const line =
      typeof item.line_total === 'number'
        ? item.line_total
        : parseFloat(String(item.line_total)) || 0
    return acc + line
  }, 0)
  const total =
    serverTotals !== null
      ? serverTotals.total
      : previewTotal !== null
        ? parseFloat(String(previewTotal)) || 0
        : localSubtotal

  // The cart bar mirrors CartSummary's green Checkout button (same py-3.5 /
  // text-lg / rounded-xl) so the products and cart views read as one consistent
  // green action. The scan button sits to its right as a compact square; with an
  // empty cart there's no bar to sit beside, so it goes full-width as a labelled
  // "Scan to add" primary action.
  return (
    <div
      className="flex-shrink-0 bg-white border-t border-gray-200 px-4 pt-3 pb-4 flex items-stretch justify-end gap-2"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      {hasItems && (
        <button
          type="button"
          onClick={() => setActiveTab('cart')}
          aria-label={
            hasBlocked
              ? `View cart, ${itemCount} item${itemCount === 1 ? '' : 's'}, an item can’t be sold`
              : `View cart, ${itemCount} item${itemCount === 1 ? '' : 's'}, ${formatCurrency(total)}`
          }
          className={`flex-1 flex items-center justify-center gap-2 text-white py-3.5 rounded-xl font-semibold text-lg transition-colors cursor-pointer ${
            hasBlocked
              ? 'bg-red-500 hover:bg-red-600 active:bg-red-700'
              : 'bg-green-500 hover:bg-green-600 active:bg-green-700'
          }`}
        >
          {hasBlocked ? <AlertTriangle size={20} /> : <ShoppingCart size={20} />}
          <span>
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </span>
          <span className="opacity-70">·</span>
          <span>{hasBlocked ? 'Check cart' : formatCurrency(total)}</span>
        </button>
      )}
      <button
        type="button"
        onClick={onScanClick}
        aria-label="Scan a barcode to add to cart"
        className={`flex items-center justify-center gap-2 py-3.5 rounded-xl bg-green-500 hover:bg-green-600 active:bg-green-700 text-white transition-colors cursor-pointer ${
          hasItems ? 'flex-shrink-0 px-4' : 'flex-1 font-semibold text-lg'
        }`}
      >
        <ScanLine size={hasItems ? 24 : 20} />
        {!hasItems && <span>Scan to add</span>}
      </button>
      {/* Quick-actions launcher (the "20%"): opens the mobile Quick Actions
          sheet so common Functions (Hold / Void / Recall / Discount / Gift
          card / Customer) are one tap from any tab. */}
      <button
        type="button"
        onClick={openQuickActions}
        aria-label="Quick actions"
        className="flex-shrink-0 px-5 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 text-gray-700 transition-colors cursor-pointer"
      >
        <LayoutGrid size={22} />
      </button>
    </div>
  )
}
